import { useState, useEffect } from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/ui/signed-avatar-image';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ListPagination } from '@/components/ListPagination';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { Badge } from '@/components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { AssignTransactionSheet } from '@/components/contracts/AssignTransactionSheet';
import { Search, MoreHorizontal, ArrowRightLeft, X, ArrowRight, Filter, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { formatBusinessDate, formatBusinessTime } from '@/lib/format';
import { toast } from 'sonner';

interface ContractRepaymentsTransactionsProps {
    contractId?: number;
    monthRange?: { from: Date; to: Date };
}

const PAGE_SIZE = 15;

type TransactionStatus = 'ALL' | 'ASSIGNED' | 'UNASSIGNED' | 'DUPLICATES';

interface UnifiedTransaction {
    id: number;
    repayment_id: number | null;
    contract_id: number | null;
    driver_name: string | null;
    driver_photo: string | null;
    amount: number;
    transaction_at: string;
    description: string | null;
    status: 'ASSIGNED' | 'UNASSIGNED';
    total_count: number;
}

export default function ContractRepaymentsTransactions({ contractId, monthRange }: ContractRepaymentsTransactionsProps) {
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<TransactionStatus>('ALL');
    const [currentPage, setCurrentPage] = useState(1);
    const debouncedSearch = useDebounce(searchTerm, 300);

    // State for dialogs/sheets
    const [assignSheetOpen, setAssignSheetOpen] = useState(false);
    const [singleAssignTransaction, setSingleAssignTransaction] = useState<UnifiedTransaction | null>(null);
    const [unassignRecord, setUnassignRecord] = useState<UnifiedTransaction | null>(null);
    const [reassignRecord, setReassignRecord] = useState<UnifiedTransaction | null>(null);

    const fromDate = monthRange ? format(monthRange.from, 'yyyy-MM-dd') : null;
    const toDate = monthRange ? format(monthRange.to, 'yyyy-MM-dd') : null;

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch, contractId, fromDate, toDate, statusFilter]);

    // Fetch unified transactions
    const { data: recordsData, isLoading } = useQuery({
        queryKey: ['all-contract-transactions', contractId, currentPage, debouncedSearch, fromDate, toDate, statusFilter],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_all_contract_transactions', {
                p_contract_id: contractId || null,
                p_from_date: fromDate,
                p_to_date: toDate,
                p_search: debouncedSearch || null,
                p_page: currentPage,
                p_page_size: PAGE_SIZE,
                p_status: statusFilter,
            });

            if (error) throw error;
            return (data || []) as UnifiedTransaction[];
        },
    });

    const transactions = recordsData || [];
    const totalCount = transactions.length > 0 ? transactions[0].total_count : 0;
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    // THE RPC RETURNS NO CURRENCY, and the bank feed holds USD credits as well as cedis. Each row's
    // currency is read for the page's ids, so a USD credit is printed as dollars rather than as the
    // same number of cedis. It is a side read: while it loads, or if it fails, the list the RPC
    // returned still renders and an amount whose currency is unknown is printed bare, never as
    // cedis. (A `currency` column on the RPC is owed to the backend.)
    const pageIds = transactions.map(t => t.id);
    const { data: currencyById, isError: currencyFailed } = useQuery({
        queryKey: ['all-contract-transactions-currency', pageIds],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('transactions')
                .select('id, currency')
                .in('id', pageIds);
            if (error) throw error;
            return Object.fromEntries((data || []).map(c => [c.id, c.currency])) as Record<number, string>;
        },
        enabled: pageIds.length > 0,
    });

    // Stats query - uses dedicated RPC to calculate stats across all transactions
    const { data: statsData } = useQuery({
        queryKey: ['transaction-stats', contractId, fromDate, toDate],
        queryFn: async () => {
            // Use a dedicated stats query to avoid row limits
            const { data, error } = await supabase.rpc('get_contract_transaction_stats', {
                p_contract_id: contractId || null,
                p_from_date: fromDate,
                p_to_date: toDate,
            });
            if (error) throw error;

            const stats = data?.[0] || {
                total: 0,
                total_amount: 0,
                assigned_count: 0,
                unassigned_count: 0,
                assigned_amount: 0
            };

            const avgAssigned = stats.assigned_count > 0 ? stats.assigned_amount / stats.assigned_count : 0;

            return {
                total: stats.total,
                assignedAmount: stats.assigned_amount,
                assigned: stats.assigned_count,
                avgAssigned,
                unassigned: stats.unassigned_count
            };
        },
    });

    // Duplicates count query - count distinct drivers with 2+ assignments in the period
    const { data: duplicatesCount } = useQuery({
        queryKey: ['duplicates-count', contractId, fromDate, toDate],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_duplicate_driver_count', {
                p_from_date: fromDate,
                p_to_date: toDate,
            });
            if (error) throw error;
            return (data as number) ?? 0;
        },
    });

    const hasDuplicates = (duplicatesCount ?? 0) > 0;


    // UNASSIGN Mutation
    const unassignMutation = useMutation({
        mutationFn: async (repaymentId: number) => {
            const { error } = await supabase
                .from('repayments')
                .delete()
                .eq('id', repaymentId);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Transaction unassigned successfully');
            queryClient.invalidateQueries({ queryKey: ['all-contract-transactions'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions-count'] });
            setUnassignRecord(null);
        },
        onError: (error: Error) => {
            toast.error(error.message);
        },
    });

    const handleUnassign = () => {
        if (unassignRecord?.repayment_id) {
            unassignMutation.mutate(unassignRecord.repayment_id);
        }
    };

    const handleAssignSingle = (t: UnifiedTransaction) => {
        setSingleAssignTransaction(t);
        setAssignSheetOpen(true);
    };

    const handleSheetClose = (open: boolean) => {
        setAssignSheetOpen(open);
        if (!open) {
            setSingleAssignTransaction(null);
            // Refresh
            queryClient.invalidateQueries({ queryKey: ['all-contract-transactions'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions-count'] });
        }
    };

    const handleReassignClose = (open: boolean) => {
        if (!open) {
            setReassignRecord(null);
            queryClient.invalidateQueries({ queryKey: ['all-contract-transactions'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions-count'] });
        }
    };

    // In the row's own currency; bare (a number, not cedis) where its currency has not been read.
    const formatAmount = (amount: number, currency: string | undefined) => currency
        ? new Intl.NumberFormat('en-GH', { style: 'currency', currency }).format(amount)
        : new Intl.NumberFormat('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

    return (
        <div className="space-y-6">
            {/* Summary Stats */}
            <div className="grid gap-4 md:grid-cols-5">
                <StatCard
                    title="Assigned Amount"
                    value={<CurrencyDisplay amount={statsData?.assignedAmount || 0} />}
                    description="The combined value of all assigned payment transactions in the period."
                />
                <StatCard
                    title="Avg Assigned"
                    value={<CurrencyDisplay amount={statsData?.avgAssigned || 0} />}
                    description="The mean value of assigned transactions. Useful for spotting unusual payments that may need review."
                />
                <StatCard
                    title="Total Transactions"
                    value={statsData?.total || 0}
                    description="The total count of payment transactions (both assigned and unassigned) within the selected date range."
                />
                <StatCard
                    title="Assigned"
                    value={statsData?.assigned || 0}
                    description="The number of transactions that have been linked to a driver contract."
                />
                <StatCard
                    title="Unassigned"
                    value={statsData?.unassigned || 0}
                    description="The number of transactions not yet linked to any contract. These payments need to be assigned to update driver balances."
                />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex flex-1 items-center gap-2 w-full">
                    <div className="relative flex-1 max-w-sm">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search by description, driver..."
                            className="pl-9"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <Select
                        value={statusFilter}
                        onValueChange={(val) => setStatusFilter(val as TransactionStatus)}
                    >
                        <SelectTrigger className="w-[160px]">
                            <div className="flex items-center gap-2">
                                {hasDuplicates && statusFilter !== 'DUPLICATES' ? (
                                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                                ) : (
                                    <Filter className="h-4 w-4 text-muted-foreground" />
                                )}
                                <SelectValue placeholder="Status" />
                            </div>
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="ALL">All Status</SelectItem>
                            <SelectItem value="ASSIGNED">Assigned</SelectItem>
                            <SelectItem value="UNASSIGNED">Unassigned</SelectItem>
                            <SelectItem value="DUPLICATES">
                                <span className="flex items-center gap-2">
                                    Duplicates
                                    {hasDuplicates && (
                                        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-100 px-1.5 text-xs font-medium text-amber-700">
                                            {duplicatesCount}
                                        </span>
                                    )}
                                </span>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {totalPages > 0 && (
                    <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        totalItems={totalCount}
                    />
                )}
            </div>

            <div>
                {isLoading ? (
                    <Skeleton className="h-48 w-full" />
                ) : transactions.length > 0 ? (
                    <>
                    {currencyFailed && (
                        <p className="mb-2 text-xs text-muted-foreground">
                            The currencies of these transactions could not be read, so their amounts are shown without one.
                        </p>
                    )}
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                                <TableHead className="pl-8">Driver</TableHead>
                                <TableHead className="w-12"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {transactions.map((t) => {
                                const isUnassigned = t.status === 'UNASSIGNED';
                                return (
                                    <TableRow key={`${t.id}-${t.repayment_id || 'u'}`}>
                                        <TableCell>
                                            <div className="flex flex-col">
                                                <span>{formatBusinessDate(t.transaction_at)}</span>
                                                <span className="text-xs text-muted-foreground">{formatBusinessTime(t.transaction_at)}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="max-w-[320px]">
                                            <span className="text-sm break-words whitespace-pre-wrap">
                                                {t.description || '-'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <span className={`font-medium ${isUnassigned ? 'text-green-600 dark:text-green-400' : ''}`}>
                                                {formatAmount(t.amount, currencyById?.[t.id])}
                                            </span>
                                        </TableCell>
                                        <TableCell className="pl-8">
                                            {t.contract_id ? (
                                                <div className="flex items-center gap-2">
                                                    <Avatar className="h-7 w-7 shrink-0">
                                                        <SignedAvatarImage src={t.driver_photo || undefined} alt={t.driver_name || ''} />
                                                        <AvatarFallback className="text-xs">
                                                            {(t.driver_name || '').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div className="flex flex-col">
                                                        <span className="font-medium">{t.driver_name}</span>
                                                        <span className="text-xs text-muted-foreground">Contract #{t.contract_id}</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {isUnassigned ? (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleAssignSingle(t)}
                                                >
                                                    Assign
                                                </Button>
                                            ) : (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                                            <MoreHorizontal className="h-4 w-4" />
                                                            <span className="sr-only">Actions</span>
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem onClick={() => setReassignRecord(t)}>
                                                            <ArrowRightLeft className="h-4 w-4 mr-2" />
                                                            Reassign
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem
                                                            onClick={() => setUnassignRecord(t)}
                                                            className="text-destructive focus:text-destructive"
                                                        >
                                                            <X className="h-4 w-4 mr-2" />
                                                            Unassign
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    </>
                ) : (
                    <div className="text-center py-12 border-2 border-dashed rounded-lg">
                        <p className="text-muted-foreground">No transactions found</p>
                    </div>
                )}
            </div>

            {/* Unassign Confirmation */}
            <AlertDialog open={!!unassignRecord} onOpenChange={(open) => !open && setUnassignRecord(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Unassign Transaction?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will remove the transaction from Contract #{unassignRecord?.contract_id}.
                            It will appear as 'Unassigned' and can be reassigned later.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleUnassign}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Unassign
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Assign/Reassign Sheet */}
            {(assignSheetOpen || !!reassignRecord) && (
                <AssignTransactionSheet
                    open={assignSheetOpen || !!reassignRecord}
                    onOpenChange={assignSheetOpen ? handleSheetClose : handleReassignClose}
                    transaction={
                        reassignRecord
                            ? {
                                id: reassignRecord.id, // Transaction ID
                                amount: reassignRecord.amount,
                                currency: currencyById?.[reassignRecord.id],
                                transaction_at: reassignRecord.transaction_at,
                                description: reassignRecord.description
                            }
                            : (singleAssignTransaction ? {
                                id: singleAssignTransaction.id,
                                amount: singleAssignTransaction.amount,
                                currency: currencyById?.[singleAssignTransaction.id],
                                transaction_at: singleAssignTransaction.transaction_at,
                                description: singleAssignTransaction.description
                            } : null)
                    }
                    preSelectedContractId={contractId || null}
                />
            )}
        </div>
    );
}
